import {ANON_ROLE_UID} from "@buildwithflux/constants";
import {
    isBareDocumentUid,
    isOrganizationUid,
    isPrefixedUserUid,
    type BareDocumentUid,
    type OrganizationUid,
    type PrefixedUserUid,
} from "@buildwithflux/identifiers-schema";
import {z} from "zod";

import {AccessListIdentifier} from "./accessList";
import {DocumentPermissionsData} from "./documentPermissions";
import {PermissionType} from "./permissionType";

/**
 * Append-only permissions journal: every staged sharing change is one
 * immutable record at `documents/{uid}/permissionsJournal/{recordUid}`.
 * Records are grouped by `draft_uid` (one share-dialog session), delivered in
 * batches, and only materialize into the roles/invites subcollections (the
 * committed view) when the draft is committed. A record's change payload is
 * never mutated after creation; only its lifecycle status is stamped.
 *
 * These zod schemas are the canonical structural validators for journal
 * changes. The HTTP wire shapes for the accounts-app permissions-journal
 * endpoints live in `./effect/permissionsJournal`; changes cross the wire
 * opaquely and are validated here at the handler boundary so role payloads go
 * through the same `DocumentPermissionsData` normalization pipeline as every
 * other role write.
 */

const PermissionTypeValue = z.nativeEnum(PermissionType);

// zod carriers for the canonical Effect-branded identifiers: structural
// runtime validation is delegated to the branded schemas' guards, so the
// inferred types are the branded types themselves, not loose strings.
const BareDocumentUidValue = z.custom<BareDocumentUid>(
    (value) => typeof value === "string" && isBareDocumentUid(value),
    "Expected a bare 36-character document UID",
);
const PrefixedUserUidValue = z.custom<PrefixedUserUid>(
    (value) => typeof value === "string" && isPrefixedUserUid(value),
    'Expected a prefixed ("usr_…") user UID',
);
const OrganizationUidValue = z.custom<OrganizationUid>(
    (value) => typeof value === "string" && isOrganizationUid(value),
    'Expected an organization ("org_…") UID',
);

/**
 * The principals a journal change can target: the normalized `account_uid`
 * forms produced by `DocumentPermissionsData` (prefixed user, organization,
 * or the anonymous role). Legacy bare user UIDs never cross this wire — the
 * dialog diffs against the normalized committed view.
 */
export const PermissionsJournalPrincipalUid = z.union([
    PrefixedUserUidValue,
    OrganizationUidValue,
    z.literal(ANON_ROLE_UID),
]);
export type PermissionsJournalPrincipalUid = z.infer<typeof PermissionsJournalPrincipalUid>;

/**
 * Structural validator for a project invite crossing the permissions-journal
 * wire. Mirrors `IProjectInviteData` in `@buildwithflux/models` (which cannot
 * be imported here without inverting the layering).
 */
export const PermissionsJournalInviteData = z.object({
    uid: z.string().min(1),
    document_uid: z.string().min(1),
    document_name: z.string().optional(),
    invitee_email: z.string().email(),
    permission_type: PermissionTypeValue,
    inviter_uid: z.string().min(1),
    inviter_handle: z.string().min(1),
    inviter_name: z.string().optional(),
    inviter_email: z.string().optional(),
    status: z.union([z.literal("created"), z.literal("accepted")]),
    organization_uid: OrganizationUidValue.optional(),
    created_at: z.number(),
    updated_at: z.number(),
});
export type PermissionsJournalInviteData = z.infer<typeof PermissionsJournalInviteData>;

export const PermissionsJournalChangeData = z.discriminatedUnion("kind", [
    z.object({kind: z.literal("set_role"), role: DocumentPermissionsData}),
    z.object({kind: z.literal("withdraw_role"), account_uid: PermissionsJournalPrincipalUid}),
    /**
     * Tombstones every non-owner role, resolved server-side from the
     * authoritative roles collection (never from a client snapshot).
     */
    z.object({kind: z.literal("set_document_private")}),
    z.object({kind: z.literal("set_invite"), invite: PermissionsJournalInviteData, send_email: z.boolean()}),
    z.object({kind: z.literal("remove_invite"), invite_uid: z.string().min(1)}),
    z.object({kind: z.literal("set_organization_member_permission_type"), permission_type: PermissionTypeValue}),
    z.object({kind: z.literal("set_enterprise_member_permission_type"), permission_type: PermissionTypeValue}),
]);
export type PermissionsJournalChangeData = z.infer<typeof PermissionsJournalChangeData>;

/** One staged permission change, materialized in `seq` order on commit. */
export type PermissionsJournalChange = PermissionsJournalChangeData;

/**
 * Maximum changes accepted per stage request. A re-stage supersedes the prior
 * stage's open records (at most this many stamps) in the same Firestore batch
 * as the new records (at most this many creates), so the cap keeps the
 * combined batch within Firestore's 500-operation limit.
 */
export const PERMISSIONS_JOURNAL_MAX_CHANGES_PER_STAGE = 250;

export const PermissionsJournalChangesData = z
    .array(PermissionsJournalChangeData)
    .max(PERMISSIONS_JOURNAL_MAX_CHANGES_PER_STAGE);

export const PermissionsJournalStatusValues = ["open", "committed", "discarded"] as const;
export type PermissionsJournalStatusData = (typeof PermissionsJournalStatusValues)[number];

/**
 * One immutable journal record: a single staged permission change plus the
 * grouping/order/lifecycle metadata needed to commit or discard it as part of
 * its draft. The `change` payload is write-once; only `status` and the
 * corresponding timestamp are stamped on commit/discard (a re-staged draft
 * stamps its superseded records `discarded`).
 *
 * This type is deliberately standalone — it must never be embedded in or
 * referenced from any document data model (`IDocumentData` etc.).
 */
export const PermissionsJournalRecordData = z.object({
    uid: z.string().min(1),
    document_uid: BareDocumentUidValue,
    draft_uid: z.string().min(1),
    seq: z.number().int().nonnegative(),
    status: z.enum(PermissionsJournalStatusValues),
    change: PermissionsJournalChangeData,
    /** The acting user, always in the canonical prefixed (`usr_…`) form. */
    created_by: PrefixedUserUidValue,
    created_at: z.number(),
    committed_at: z.number().optional(),
    discarded_at: z.number().optional(),
});
export type PermissionsJournalRecordData = z.infer<typeof PermissionsJournalRecordData>;

/** The base-document permission fields a journal commit may materialize. */
export interface PermissionsJournalDocumentUpdate {
    organization_member_permission_type?: PermissionType;
    enterprise_member_permission_type?: PermissionType;
    user_access_list?: AccessListIdentifier[];
    has_anon_access?: boolean;
}
